import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Link,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import SearchableSelect from 'shared/components/SearchableSelect';
import { alpha, useTheme } from '@mui/material/styles';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { toast } from 'react-hot-toast';
import { fetchActiveTenantUsers } from 'shared/api/services/tenantUserService';
import { getRegistryTenantIdFromSession } from 'shared/utils/sessionProfileStore';
import {
  MONITORING_REVIEW_STATUS,
  MONITORING_RISK_RATING,
  MONITORING_TYPE,
  REVIEW_FREQUENCY,
  formatEnumLabel,
} from '../constants/enums';
import { listStatusParams, monitoringReviewApi, systemDocumentApi } from '../api/systemRegisterApi';
import { apiErrorMessage, unwrapData, unwrapList } from '../utils/responseHelpers';
import { resolveCentralOrigin } from 'shared/api/apiOrigin';
import FormSection from './FormSection';

export const emptyMonitoringReviewRow = (reviewer = '') => ({
  key: `mr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  id: null,
  monitoringReviewId: null,
  monitoringType: '',
  reviewAreaControl: '',
  standardControlReference: '',
  reviewFrequency: '',
  observationFindings: '',
  riskRating: '',
  correctiveActionRequired: '',
  actionOwnerEmail: '',
  status: 'OPEN',
  nextReviewDate: '',
  evidenceFileName: '',
  evidenceContentType: '',
  evidenceLocation: '',
  reviewer: reviewer || '',
  approvals: [],
});

const userFilter = createFilterOptions({
  stringify: (option) =>
    `${option.name ?? ''} ${option.email ?? ''} ${option.id ?? ''}`.toLowerCase(),
});

const userLabel = (u) => {
  if (!u) return '';
  const name = (u.name || '').trim();
  const email = (u.email || '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '';
};

const findUserOption = (users, value) => {
  const raw = (value || '').trim();
  if (!raw) return null;
  const byEmail = users.find((u) => (u.email || '').trim().toLowerCase() === raw.toLowerCase());
  if (byEmail) return byEmail;
  const byName = users.find((u) => (u.name || '').trim().toLowerCase() === raw.toLowerCase());
  if (byName) return byName;
  const byLabel = users.find((u) => userLabel(u).toLowerCase() === raw.toLowerCase());
  if (byLabel) return byLabel;
  return { id: `legacy-${raw}`, name: raw, email: raw.includes('@') ? raw : '' };
};

const toDateInputValue = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

/** Next review date from today, based on catalog / selected frequency. */
export const nextReviewDateFromFrequency = (frequency, from = new Date()) => {
  const key = String(frequency || '').trim().toUpperCase();
  if (!key || key === 'AD_HOC') return '';
  const date = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  switch (key) {
    case 'WEEKLY':
      date.setDate(date.getDate() + 7);
      break;
    case 'MONTHLY':
      date.setMonth(date.getMonth() + 1);
      break;
    case 'QUARTERLY':
      date.setMonth(date.getMonth() + 3);
      break;
    case 'SEMI_ANNUAL':
      date.setMonth(date.getMonth() + 6);
      break;
    case 'ANNUAL':
      date.setFullYear(date.getFullYear() + 1);
      break;
    default:
      return '';
  }
  return toDateInputValue(date);
};

const mapCatalogToForm = (catalog, prev = {}) => {
  const reviewFrequency = catalog?.reviewFrequency ?? '';
  return {
    ...prev,
    monitoringReviewId: catalog?.id ?? null,
    monitoringType: catalog?.monitoringType ?? '',
    reviewAreaControl: catalog?.reviewAreaControl ?? '',
    standardControlReference: catalog?.standardControlReference ?? '',
    reviewFrequency,
    nextReviewDate: nextReviewDateFromFrequency(reviewFrequency),
  };
};

function fileHref(location) {
  if (!location) return null;
  if (location.startsWith('http://') || location.startsWith('https://')) return location;
  return `${resolveCentralOrigin()}${location.startsWith('/') ? '' : '/'}${location}`;
}

/**
 * Create / edit / view dialog for a monitoring review entry.
 * When `selectFromRegister` is true, user picks an existing Monitoring Review catalog entry.
 */
export default function MonitoringReviewDialog({
  open,
  mode = 'create',
  initialValues,
  reviewerName = '',
  saving = false,
  onClose,
  onSave,
  selectFromRegister = false,
  excludeMonitoringReviewIds = [],
}) {
  const theme = useTheme();
  const readOnly = mode === 'view';
  const [values, setValues] = useState(() => emptyMonitoringReviewRow(reviewerName));
  const [uploading, setUploading] = useState(false);
  const [errors, setErrors] = useState({});
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [catalogOptions, setCatalogOptions] = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const fileInputRef = useRef(null);

  const excludedIds = useMemo(
    () => new Set((excludeMonitoringReviewIds || []).map((id) => String(id)).filter(Boolean)),
    [excludeMonitoringReviewIds]
  );

  const loadTenantUsers = useCallback(async () => {
    const registryTenantId = getRegistryTenantIdFromSession();
    if (!registryTenantId) {
      setTenantUsers([]);
      return;
    }
    setLoadingUsers(true);
    try {
      const rows = await fetchActiveTenantUsers(registryTenantId, { page: 0, size: 500 });
      setTenantUsers(rows);
    } catch (error) {
      const msg =
        error.response?.data?.message || error.message || 'Failed to load tenant users';
      toast.error(typeof msg === 'string' ? msg : 'Failed to load users');
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  const loadCatalog = useCallback(async () => {
    if (!selectFromRegister) {
      setCatalogOptions([]);
      return;
    }
    setLoadingCatalog(true);
    try {
      const response = await monitoringReviewApi.list(listStatusParams(false));
      setCatalogOptions(unwrapList(response));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to load monitoring reviews.'));
      setCatalogOptions([]);
    } finally {
      setLoadingCatalog(false);
    }
  }, [selectFromRegister]);

  useEffect(() => {
    if (!open) return;
    loadTenantUsers();
    loadCatalog();
    const seed = {
      ...emptyMonitoringReviewRow(reviewerName),
      ...(initialValues || {}),
      reviewer: initialValues?.reviewer || reviewerName || '',
    };
    setValues(seed);
    setErrors({});
  }, [open, initialValues, reviewerName, loadTenantUsers, loadCatalog]);

  const availableCatalog = useMemo(() => {
    const currentId =
      values.monitoringReviewId != null ? String(values.monitoringReviewId) : null;
    return catalogOptions.filter((r) => {
      if (r?.id == null) return false;
      const id = String(r.id);
      if (currentId && id === currentId) return true;
      return !excludedIds.has(id);
    });
  }, [catalogOptions, excludedIds, values.monitoringReviewId]);

  const linkedFromCatalog = Boolean(selectFromRegister && values.monitoringReviewId);

  const actionOwnerOption = useMemo(
    () => findUserOption(tenantUsers, values.actionOwnerEmail),
    [tenantUsers, values.actionOwnerEmail]
  );

  const setField = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    if (selectFromRegister && !values.monitoringReviewId) {
      next.monitoringReviewId = 'Select a review area';
    }
    if (!values.monitoringType) next.monitoringType = 'Select review type';
    if (!(values.reviewAreaControl || '').trim()) next.reviewAreaControl = 'Required';
    if (!(values.actionOwnerEmail || '').trim()) {
      next.actionOwnerEmail = 'Select a user';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.actionOwnerEmail.trim())) {
      next.actionOwnerEmail = 'Selected user must have a valid email';
    }
    if (!values.status) next.status = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (readOnly) {
      onClose?.();
      return;
    }
    if (!validate() || saving) return;
    await onSave?.({
      ...values,
      monitoringReviewId: values.monitoringReviewId ?? null,
      reviewAreaControl: values.reviewAreaControl.trim(),
      standardControlReference: values.standardControlReference?.trim() || '',
      observationFindings: values.observationFindings?.trim() || '',
      correctiveActionRequired: values.correctiveActionRequired?.trim() || '',
      actionOwnerEmail: values.actionOwnerEmail.trim(),
      reviewer: values.reviewer || reviewerName || '',
    });
  };

  const handleEvidencePick = async (file) => {
    if (!file || readOnly) return;
    setUploading(true);
    try {
      const response = await systemDocumentApi.upload(file);
      const uploaded = unwrapData(response);
      if (!uploaded?.location) throw new Error('Upload did not return a file location');
      setValues((prev) => ({
        ...prev,
        evidenceFileName: uploaded.fileName || file.name,
        evidenceContentType: uploaded.contentType || file.type || '',
        evidenceLocation: uploaded.location,
      }));
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to upload evidence'));
    } finally {
      setUploading(false);
    }
  };

  const title =
    mode === 'view'
      ? 'View Software Monitoring And Review'
      : mode === 'edit'
        ? 'Edit Software Monitoring And Review'
        : 'Add Software Monitoring And Review';

  const evidenceHref = fileHref(values.evidenceLocation);

  const fieldSx = { '& .MuiFormHelperText-root': { mx: 0 } };
  const selectSx = {
    ...fieldSx,
    minWidth: '100%',
    width: '100%',
    minHeight: 48,
    '& .MuiInputLabel-root': { whiteSpace: 'nowrap' },
    '& .MuiSelect-select': {
      minHeight: '1.5em',
      display: 'flex',
      alignItems: 'center',
    },
  };

  const handleReviewAreaSelect = (catalogId) => {
    if (!catalogId) {
      setValues((prev) => ({
        ...emptyMonitoringReviewRow(reviewerName),
        key: prev.key,
        id: prev.id,
        approvals: prev.approvals || [],
        reviewer: prev.reviewer || reviewerName || '',
      }));
      setErrors((prev) => {
        const next = { ...prev };
        delete next.monitoringReviewId;
        delete next.reviewAreaControl;
        return next;
      });
      return;
    }
    const option =
      availableCatalog.find((r) => String(r.id) === String(catalogId))
      || catalogOptions.find((r) => String(r.id) === String(catalogId));
    if (!option) return;
    setValues((prev) =>
      mapCatalogToForm(option, {
        ...prev,
        key: prev.key,
        id: prev.id,
        approvals: prev.approvals || [],
        reviewer: prev.reviewer || reviewerName || '',
      })
    );
    setErrors({});
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{title}</DialogTitle>
      <DialogContent dividers sx={{ bgcolor: 'background.paper', py: 2 }}>
        <FormSection title="Review details">
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              {selectFromRegister ? (
                <SearchableSelect
                  label="Review Area"
                  size="medium"
                  required
                  disabled={readOnly || loadingCatalog}
                  value={values.monitoringReviewId != null ? String(values.monitoringReviewId) : ''}
                  onChange={(next) => handleReviewAreaSelect(next)}
                  error={Boolean(errors.monitoringReviewId || errors.reviewAreaControl)}
                  helperText={errors.monitoringReviewId || errors.reviewAreaControl}
                  placeholder={loadingCatalog ? 'Loading…' : 'Select review area…'}
                  sx={selectSx}
                  options={availableCatalog.map((opt) => ({
                    value: String(opt.id),
                    label: (opt.reviewAreaControl || '').trim() || `Review #${opt.id}`,
                  }))}
                />
              ) : (
                <TextField
                  label="Review Area"
                  fullWidth
                  size="small"
                  required
                  disabled={readOnly}
                  value={values.reviewAreaControl || ''}
                  onChange={(e) => setField('reviewAreaControl', e.target.value)}
                  error={Boolean(errors.reviewAreaControl)}
                  helperText={errors.reviewAreaControl}
                  sx={fieldSx}
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                label="Review type"
                size="medium"
                required
                disabled={readOnly || linkedFromCatalog}
                value={values.monitoringType || ''}
                onChange={(next) => setField('monitoringType', next)}
                error={Boolean(errors.monitoringType)}
                helperText={errors.monitoringType}
                sx={selectSx}
                options={MONITORING_TYPE.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                label="Review frequency"
                size="medium"
                disabled={readOnly || linkedFromCatalog}
                value={values.reviewFrequency || ''}
                onChange={(next) => {
                  setValues((prev) => ({
                    ...prev,
                    reviewFrequency: next,
                    nextReviewDate: nextReviewDateFromFrequency(next),
                  }));
                  setErrors((prev) => {
                    if (!prev.reviewFrequency) return prev;
                    const copy = { ...prev };
                    delete copy.reviewFrequency;
                    return copy;
                  });
                }}
                sx={selectSx}
                options={REVIEW_FREQUENCY.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Standard / control reference"
                fullWidth
                size="small"
                disabled={readOnly || linkedFromCatalog}
                value={values.standardControlReference || ''}
                onChange={(e) => setField('standardControlReference', e.target.value)}
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Findings & actions">
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                label="Risk rating"
                size="medium"
                disabled={readOnly}
                value={values.riskRating || ''}
                onChange={(next) => setField('riskRating', next)}
                sx={selectSx}
                options={MONITORING_RISK_RATING.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <SearchableSelect
                label="Status"
                size="medium"
                required
                disabled={readOnly}
                allowEmpty={false}
                value={values.status || ''}
                onChange={(next) => setField('status', next)}
                error={Boolean(errors.status)}
                helperText={errors.status}
                sx={selectSx}
                options={MONITORING_REVIEW_STATUS.map((opt) => ({ value: opt, label: formatEnumLabel(opt) }))}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Observation / findings"
                fullWidth
                size="small"
                multiline
                minRows={3}
                disabled={readOnly}
                value={values.observationFindings || ''}
                onChange={(e) => setField('observationFindings', e.target.value)}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Corrective action required"
                fullWidth
                size="small"
                multiline
                minRows={2}
                disabled={readOnly}
                value={values.correctiveActionRequired || ''}
                onChange={(e) => setField('correctiveActionRequired', e.target.value)}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Ownership & schedule">
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <Autocomplete
                size="small"
                options={tenantUsers}
                loading={loadingUsers}
                disabled={readOnly}
                filterOptions={userFilter}
                value={actionOwnerOption}
                onChange={(_, option) =>
                  setField('actionOwnerEmail', (option?.email || '').trim())
                }
                getOptionLabel={(option) => userLabel(option)}
                isOptionEqualToValue={(a, b) => String(a?.id) === String(b?.id)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    label="Action owner"
                    required
                    error={Boolean(errors.actionOwnerEmail)}
                    helperText={errors.actionOwnerEmail}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                    sx={fieldSx}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Next review date"
                fullWidth
                size="small"
                type="date"
                disabled={readOnly}
                InputLabelProps={{ shrink: true }}
                value={values.nextReviewDate || ''}
                onChange={(e) => setField('nextReviewDate', e.target.value)}
                helperText={
                  values.reviewFrequency && values.reviewFrequency !== 'AD_HOC'
                    ? `Filled from ${formatEnumLabel(values.reviewFrequency).toLowerCase()} frequency`
                    : values.reviewFrequency === 'AD_HOC'
                      ? 'Ad-hoc reviews have no scheduled date'
                      : undefined
                }
                sx={fieldSx}
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title="Evidence (optional)">
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              handleEvidencePick(file);
            }}
          />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
              p: 2,
              borderRadius: 2,
              border: `1px dashed ${alpha(theme.palette.primary.main, 0.35)}`,
              bgcolor: theme.palette.mode === 'dark'
                ? 'background.paper'
                : alpha(theme.palette.primary.main, 0.03),
            }}
          >
            <Box display="flex" alignItems="center" gap={1.25} minWidth={0} flex={1}>
              <InsertDriveFileOutlinedIcon color="action" />
              <Box minWidth={0}>
                {values.evidenceFileName ? (
                  evidenceHref ? (
                    <Link
                      href={evidenceHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      fontWeight={600}
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {values.evidenceFileName}
                    </Link>
                  ) : (
                    <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                      {values.evidenceFileName}
                    </Typography>
                  )
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No evidence attached yet
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block">
                  Attach supporting documents if available
                </Typography>
              </Box>
            </Box>
            {!readOnly ? (
              <Button
                size="small"
                variant="contained"
                startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <AttachFileIcon />}
                disabled={uploading}
                onClick={() => fileInputRef.current?.click()}
              >
                {values.evidenceFileName ? 'Replace file' : 'Attach file'}
              </Button>
            ) : null}
          </Box>
        </FormSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={onClose} color="inherit" variant="outlined" disabled={saving}>
          {readOnly ? 'Close' : 'Cancel'}
        </Button>
        {!readOnly ? (
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
